import { createTheme, ThemeOptions } from '@mui/material/styles';

export interface CustomTheme {
  mode: 'light' | 'dark';
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

export const createCustomTheme = (customTheme: CustomTheme) => {
  const { mode, primaryColor, secondaryColor, accentColor } = customTheme;
  
  const isDark = mode === 'dark';
  
  const themeOptions: ThemeOptions = {
    palette: {
      mode: mode,
      primary: {
        main: primaryColor,
        light: isDark ? '#4a5568' : '#e2e8f0',
        dark: isDark ? '#1a202c' : '#2d3748',
        contrastText: isDark ? '#ffffff' : '#000000',
      },
      secondary: {
        main: secondaryColor,
        light: isDark ? '#4a5568' : '#e2e8f0',
        dark: isDark ? '#1a202c' : '#2d3748',
        contrastText: isDark ? '#ffffff' : '#000000',
      },
      background: {
        default: isDark ? '#0f0f0f' : '#f0f4f8',
        paper: isDark ? '#1a1a1a' : '#f8fbff',
      },
      text: {
        primary: isDark ? '#ffffff' : '#1a1a1a',
        secondary: isDark ? '#a0a0a0' : '#666666',
      },
      // Custom accent color - we'll use error for accent
      error: {
        main: accentColor,
        light: isDark ? '#4a5568' : '#e2e8f0',
        dark: isDark ? '#1a202c' : '#2d3748',
        contrastText: isDark ? '#ffffff' : '#000000',
      },
    },
    typography: {
      fontFamily: '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", "Tahoma", "Verdana", sans-serif',
      h1: {
        fontSize: '2.75rem',
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 600,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '0em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.35,
        letterSpacing: '0em',
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '0em',
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '0em',
      },
      body1: {
        fontSize: '0.9375rem',
        fontWeight: 400,
        lineHeight: 1.6,
        letterSpacing: '0.01em',
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: '0.01em',
      },
      button: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${primaryColor}dd 0%, ${accentColor}dd 100%)`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isDark 
              ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
              : '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: isDark ? '1px solid #333' : '1px solid #e0e0e0',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: primaryColor,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: primaryColor,
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: isDark 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' 
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: isDark 
              ? '0 2px 20px rgba(0, 0, 0, 0.3)' 
              : '0 2px 20px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

export const defaultTheme: CustomTheme = {
  mode: 'light',
  primaryColor: '#1E3A8A', // Dark Blue
  secondaryColor: '#065F46', // Dark Emerald
  accentColor: '#D97706', // Dark Amber/Gold
};

export const classicalThemes = {
  royal: {
    mode: 'light' as const,
    primaryColor: '#8b5cf6', // Purple
    secondaryColor: '#f59e0b', // Amber
    accentColor: '#10b981', // Emerald
  },
  elegant: {
    mode: 'light' as const,
    primaryColor: '#1f2937', // Gray
    secondaryColor: '#dc2626', // Red
    accentColor: '#f59e0b', // Amber
  },
  modern: {
    mode: 'light' as const,
    primaryColor: '#0078D4', // Microsoft Blue
    secondaryColor: '#107C10', // Microsoft Green
    accentColor: '#FFB900', // Microsoft Gold
  },
  professional: {
    mode: 'light' as const,
    primaryColor: '#005A9E', // Professional Blue
    secondaryColor: '#004B87', // Deep Blue
    accentColor: '#E81123', // Microsoft Red
  },
  darkRoyal: {
    mode: 'dark' as const,
    primaryColor: '#8b5cf6', // Purple
    secondaryColor: '#f59e0b', // Amber
    accentColor: '#10b981', // Emerald
  },
  darkElegant: {
    mode: 'dark' as const,
    primaryColor: '#1f2937', // Gray
    secondaryColor: '#dc2626', // Red
    accentColor: '#f59e0b', // Amber
  },
  darkProfessional: {
    mode: 'dark' as const,
    primaryColor: '#0078D4', // Microsoft Blue
    secondaryColor: '#107C10', // Microsoft Green
    accentColor: '#FFB900', // Microsoft Gold
  },
};

